/**
 * Game Start Store using Zustand
 * Replaces the old RxJS-based StartGameStore with modern React state management
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useAppStore } from 'store/app/appStore';
import { externalCommunication } from 'core/services/communication/ExternalCommunication';
import { LAST_PLAYED_SECTION_NAME } from '@repo/common';
import { saveScrollAnchor } from 'shared/utils/scrollAnchor';
import type { IStartGameParams } from 'types/store/IStartGameStore';
import type { ISettings } from 'types/models/ISettings';
import { lastPlayedService } from '../../lastPlayed/services/lastPlayedService';

interface GameStartState {
  // Defense code state
  defenseCode: string | null;

  // Game start request state
  pendingGameKey: string | null;
  isStartingGame: boolean;
  isWaitingForDefenseCode: boolean;
  lastStartAttempt: number;
  defenseWaitTimeoutId?: number | null;
  startGameLockTimeoutId?: number | null;

  // Actions
  setDefenseCode: (code: string | null) => void;
  startGame: (gameKey: string) => void;
  clearPendingGame: () => void;

  // Utils
  saveScrollPosition: () => void;
}

const START_GAME_LOCK_MS = 5000;

/**
 * Helper function to update the last played section in the app store
 * This ensures the last played section is re-rendered with the latest games
 */
const updateLastPlayedSection = () => {
  try {
    const appStore = useAppStore.getState();
    const sections = [...appStore.sections];
    const lastPlayedIndex = sections.findIndex(section => section.name === LAST_PLAYED_SECTION_NAME);
    
    if (lastPlayedIndex !== -1) {
      // Recreate the last played section with the updated game list
      const lastPlayedSection = lastPlayedService.createLastPlayedSection(
        appStore.supportedGames,
        sections
      );
      
      if (lastPlayedSection) {
        sections[lastPlayedIndex] = lastPlayedSection;
        useAppStore.getState().setSections(sections);
      }
    }
  } catch (error) {
    console.warn('[useGameStart] Failed to update last played section:', error);
  }
};

/**
 * Helper function to reset campain settings.
 * This ensures lobby state will be correct after the user opens a game from the Tournaments Only Layout
 * and the defense code is off.
 */
const resetCampaignSettings = () => {
  try {
    const updateSettings = useAppStore.getState().updateSettings;
    const isIngameCampaignOnlyOpened = useAppStore.getState().isIngameCampaignOnlyOpened;
    const setIsIngameCampaignOnlyOpened = useAppStore.getState().setIsIngameCampaignOnlyOpened;

    if(isIngameCampaignOnlyOpened) {
      setIsIngameCampaignOnlyOpened(false);
    }

    updateSettings({
      campaignOnly: undefined
    });
  } catch (error) {
    console.warn('[useGameStart] Failed to reset camapign settings:', error);
  }
}

const DEFENSE_WAIT_TIMEOUT = 1000; // ms

// fallow-ignore-next-line unused-export
export const useGameStartStore = create<GameStartState>()(
  devtools<GameStartState>(
    (set, get) => ({
      // Initial state
      defenseCode: null as string | null,
      pendingGameKey: null,
      isStartingGame: false,
      isWaitingForDefenseCode: false,
      lastStartAttempt: 0,
      defenseWaitTimeoutId: null,
      startGameLockTimeoutId: null,
      
      // Set defense code and retry pending game if exists
      setDefenseCode: (code) => {
        const currentState = get();

        // Check if we should continue with pending game BEFORE updating state
        // Allow late-arriving defenseCode to start the game as long as we still have a pendingGameKey
        const shouldContinuePendingGame = !!code && !!currentState.pendingGameKey;

        // If we were waiting, clear any pending timeout
        if (currentState.defenseWaitTimeoutId) {
          clearTimeout(currentState.defenseWaitTimeoutId);
        }

        set({
          defenseCode: code,
          isWaitingForDefenseCode: false,
          // Keep isStartingGame true during transition to prevent duplicate starts
          isStartingGame: shouldContinuePendingGame ? true : currentState.isStartingGame
        }, false, 'setDefenseCode');

        // If we have a pending game and now we have the defense code, retry starting the game
        if (shouldContinuePendingGame) {
          // Use setTimeout to avoid potential state conflicts
          setTimeout(() => {
            const state = get();
            if (state.pendingGameKey) {
              // Continue with the game start now that we have the defense code
              state.saveScrollPosition();
              const appState = useAppStore.getState();
              const gameStartParams = buildGameStartParams(code, state.pendingGameKey, appState.settings);
              externalCommunication.sendStartLobbyGame(gameStartParams);

              set({
                pendingGameKey: null,
                isWaitingForDefenseCode: false,
                defenseWaitTimeoutId: null
              }, false, 'startGame-afterDefenseCode');
            }
          }, 0);
        }
      },
      
      // Start game with validation and debouncing
      startGame: (gameKey) => {
        const state = get();
        const now = Date.now();

        // Prevent starting if already starting
        if (state.isStartingGame) {
          return;
        }

        const appState = useAppStore.getState();
        const currentGameKey = appState.settings.gameKey;

        if (state.startGameLockTimeoutId) {
          clearTimeout(state.startGameLockTimeoutId);
        }
        const lockTimeoutId = window.setTimeout(() => {
          set({
            isStartingGame: false,
            startGameLockTimeoutId: null,
          }, false, 'startGame-lock-expired');
        }, START_GAME_LOCK_MS);

        set({
          pendingGameKey: gameKey,
          isStartingGame: true,
          lastStartAttempt: now,
          startGameLockTimeoutId: lockTimeoutId,
        }, false, 'startGame');

        // Close search overlay after a short delay so navigation is visible when returning to lobby.
        // Delayed to avoid a visual flash before the game redirect happens.
        if (appState.isSearchActive) {
          setTimeout(() => {
            const currentAppState = useAppStore.getState();
            // Only close if search is still active and we're still starting a game
            if (currentAppState.isSearchActive && get().pendingGameKey) {
              currentAppState.setSearchActive(false);
              currentAppState.setSearchQuery('');
            }
          }, 800);
        }

        // Add game to last played list
        lastPlayedService.addLastPlayedGame(gameKey);

        // If we're already in a game and selecting the same game, just hide lobby
        if (appState.settings.openType === 'ingame' && currentGameKey === gameKey) {
          externalCommunication.sendDisplay(false);

          if (lockTimeoutId) {
            clearTimeout(lockTimeoutId);
          }
          set({
            pendingGameKey: null,
            isStartingGame: false,
            isWaitingForDefenseCode: false,
            defenseWaitTimeoutId: null,
            startGameLockTimeoutId: null,
          }, false, 'startGame-hideDisplay');
          return;
        }

        // Always request defenceCode and wait briefly. Parent will either:
        // - send a defenceCode (we'll start in setDefenseCode), or
        // - handle redirect itself without sending a code (we should do nothing on timeout)
        externalCommunication.sendDefenseCodeRequired(gameKey);
        const timeoutId = window.setTimeout(() => {
          const s = get();
          if (s.pendingGameKey === gameKey && !s.defenseCode) {
            // Needed if the user opens a game from the Tournaments Only Layout
            resetCampaignSettings();

            // Parent handles opening the game and hiding the lobby when no code is provided,
            // but we need to re-render the last played section to show the newly added game
            updateLastPlayedSection();
            
            set({
              isWaitingForDefenseCode: false,
              defenseWaitTimeoutId: null,
            }, false, 'startGame-timeout-parent-handles');
          }
        }, DEFENSE_WAIT_TIMEOUT);

        set({
          isWaitingForDefenseCode: true,
          defenseWaitTimeoutId: timeoutId,
          pendingGameKey: gameKey,
        }, false, 'startGame-wait-defense');
        return;
      },
      
      // Clear pending game
      clearPendingGame: () => {
        const { defenseWaitTimeoutId, startGameLockTimeoutId } = get();
        if (defenseWaitTimeoutId) {
          clearTimeout(defenseWaitTimeoutId);
        }
        if (startGameLockTimeoutId) {
          clearTimeout(startGameLockTimeoutId);
        }
        set({
          pendingGameKey: null,
          isStartingGame: false,
          isWaitingForDefenseCode: false,
          defenseWaitTimeoutId: null,
          startGameLockTimeoutId: null,
        }, false, 'clearPendingGame');
      },
      
      // Save scroll position to localStorage (only when in multi-view)
      saveScrollPosition: () => {
        try {
          const appState = useAppStore.getState();

          // Only preserve scroll when the user starts a game from multi-view.
          // If they're in single-view, skip saving so there's nothing to restore.
          if (appState.viewMode !== 'multi') return;

          // Capture the current main lobby position as a section anchor from the DOM
          const lobbyContainer = document.querySelector('[data-view="multi"][data-section-container]') as HTMLElement;
          if (lobbyContainer) {
            saveScrollAnchor(lobbyContainer);
          }
        } catch (err) {
          // Silently fail - not critical
          console.warn('Failed to save scroll position:', err);
        }
      },
    }),
    { name: 'GameStartStore' }
  )
);

/**
 * Build game start parameters similar to old lobby
 */
function buildGameStartParams(defenseCode: string | null, gameKey: string, settings: ISettings): IStartGameParams {
  console.log('Settings from parent app:', settings);
  return {
    defenceCode: defenseCode ?? '',
    gameKey,
    language: settings.languageCode.toLowerCase(),
    bgMusicMute: settings.bgMusicMute || false,
    mute: settings.mute || false,
    redirectHost: settings.redirectHost || '',
  };
}

// Selectors for better performance
// fallow-ignore-next-line unused-export
export const useDefenseCode = () => useGameStartStore((state) => state.defenseCode);
export const usePendingGameKey = () => useGameStartStore((state) => state.pendingGameKey);
export const useIsStartingGame = () => useGameStartStore((state) => state.isStartingGame);
export const useIsWaitingForDefenseCode = () => useGameStartStore((state) => state.isWaitingForDefenseCode);

// Game start actions
export const useGameStartActions = () => useGameStartStore((state) => ({
  setDefenseCode: state.setDefenseCode,
  startGame: state.startGame,
  clearPendingGame: state.clearPendingGame,
}));
